import { forwardRef } from 'react';
import { Loader2 } from 'lucide-react';
import { Button, Tooltip, TooltipContent, TooltipTrigger } from '@ncoder/ui';
import { cn } from '@/lib/utils';

/**
 * An icon-only button that is actually usable without sight or a mouse.
 *
 * The pattern it replaces was `<Button variant="ghost" size="icon" title="Resend invitation">` —
 * repeated for every row action in the admin panel. Two problems with it, both real:
 *   - `title` is not an accessible name that assistive tech reliably announces for a button whose
 *     only content is an SVG, so those buttons were announced as just "button".
 *   - the native `title` tooltip takes ~1s to appear, is unstyled, and never appears on touch, so
 *     the label was effectively invisible to most users too.
 *
 * Here the label is both the `aria-label` and a real tooltip, so it cannot drift out of sync, and a
 * pending state swaps the icon for a spinner while keeping the button's name stable (a screen
 * reader should not hear a button rename itself to "loading" mid-action).
 *
 * Requires a `TooltipProvider` ancestor.
 */

export interface IconActionButtonProps
  extends Omit<React.ComponentProps<typeof Button>, 'children' | 'size' | 'aria-label'> {
  /** The action, phrased as a command: "Resend invitation". Used as the name AND the tooltip. */
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  isPending?: boolean;
  /**
   * Why the action is unavailable. Shown instead of `label` when disabled — a disabled control that
   * cannot say why it is disabled is the most common dead end in an admin UI.
   */
  disabledReason?: string;
  /** Marks the action as destructive: hover and focus resolve to the destructive colour. */
  tone?: 'default' | 'destructive' | 'success';
  size?: 'sm' | 'md';
}

export const IconActionButton = forwardRef<HTMLButtonElement, IconActionButtonProps>(
  function IconActionButton(
    { label, icon: Icon, isPending, disabled, disabledReason, tone = 'default', size = 'md', className, ...props },
    ref,
  ) {
    const isDisabled = disabled || isPending;
    const tooltip = isDisabled && disabledReason ? disabledReason : label;

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          {/* A disabled button fires no pointer events, so Radix would never see the hover and the
              tooltip explaining WHY it is disabled would never open. `aria-disabled` plus a
              swallowed click keeps it focusable and hoverable while still inert. */}
          <Button
            ref={ref}
            variant="ghost"
            size="icon"
            type="button"
            aria-label={label}
            aria-disabled={isDisabled || undefined}
            onClick={isDisabled ? (event) => event.preventDefault() : props.onClick}
            className={cn(
              'shrink-0 rounded-md text-muted-foreground',
              size === 'sm' ? 'h-7 w-7 [&_svg]:size-3.5' : 'h-8 w-8 [&_svg]:size-4',
              tone === 'destructive' && 'hover:bg-destructive/10 hover:text-destructive',
              tone === 'success' && 'hover:bg-success/10 hover:text-success-strong',
              tone === 'default' && 'hover:text-foreground',
              isDisabled && 'pointer-events-auto cursor-not-allowed opacity-45 hover:bg-transparent',
              className,
            )}
            {...props}
          >
            {isPending ? <Loader2 className="animate-spin" aria-hidden /> : <Icon aria-hidden />}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[240px] text-xs">
          {tooltip}
        </TooltipContent>
      </Tooltip>
    );
  },
);
